import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import { getModelType } from './getModelType';
import type { ModelType as ModelTypeEnum } from './ModelType';
import { ModelType } from './ModelType';

/** The per-model opt-ins. Named rather than positional so the two cannot be
 * swapped at a call site. */
export interface OnlinePlannerModelFlags {
  onlinePlannerThor: boolean;
  onlinePlannerSpark: boolean;
}

/**
 * Whether this arm may reach the online planner. Every Online Planning flag is
 * ANDed with this where it is read.
 *
 * Thor and Spark gate on their own flag each, since they are validated
 * separately. An unknown model denies: arm-control-bot decides at boot from the
 * robots DB, so treating unknown as RO1 here would route a Thor jog at a
 * controller that was never started.
 */
export function isOnlinePlannerAllowedForModel(
  modelType: ModelTypeEnum | null | undefined,
  flags: OnlinePlannerModelFlags,
): boolean {
  if (modelType == null) {
    return false;
  }

  if (modelType === ModelType.enum.Thor) {
    return flags.onlinePlannerThor;
  }

  if (modelType === ModelType.enum.Spark) {
    return flags.onlinePlannerSpark;
  }

  return true;
}

/** {@link isOnlinePlannerAllowedForModel} against this process's model and flags. */
export function isOnlinePlannerAllowedForThisModel(): boolean {
  return isOnlinePlannerAllowedForModel(getModelType(), {
    onlinePlannerThor: getFeatureFlagSync('onlinePlannerThor', false),
    onlinePlannerSpark: getFeatureFlagSync('onlinePlannerSpark', false),
  });
}

export interface OnlineJoggingFlags extends OnlinePlannerModelFlags {
  useTrajectoryController: boolean;
  onlinePlannerJogging: boolean;
}

/**
 * Whether jogs on this arm route through the online planner, so the planner's
 * jog barrier is what limits them. Which collision model MoveIt plans against
 * is a narrower question — see {@link isPrimitiveCollisionModelLoadedForModel},
 * which ANDs this with jog collision avoidance.
 */
export function isOnlineJoggingActiveForModel(
  modelType: ModelTypeEnum | null | undefined,
  flags: OnlineJoggingFlags,
): boolean {
  return (
    flags.useTrajectoryController &&
    flags.onlinePlannerJogging &&
    isOnlinePlannerAllowedForModel(modelType, flags)
  );
}

/** {@link isOnlineJoggingActiveForModel} against this process's model and flags. */
export function isOnlineJoggingActiveForThisModel(): boolean {
  return isOnlineJoggingActiveForModel(getModelType(), {
    useTrajectoryController: getFeatureFlagSync(
      'useTrajectoryController',
      false,
    ),
    onlinePlannerJogging: getFeatureFlagSync('onlinePlannerJogging', false),
    onlinePlannerThor: getFeatureFlagSync('onlinePlannerThor', false),
    onlinePlannerSpark: getFeatureFlagSync('onlinePlannerSpark', false),
  });
}

export interface PrimitiveCollisionModelFlags extends OnlineJoggingFlags {
  onlinePlannerJoggingCollisionAvoidance: boolean;
}

/**
 * Whether MoveIt2 plans against the primitive (capsule/sphere) collision model
 * rather than the padded meshes.
 *
 * ros2-control-bot loads the primitives only when the jog barrier is going to
 * enforce them: online jogging active AND jog collision avoidance on. With
 * avoidance off nothing checks the capsules, so the meshes — the more faithful
 * surface — are what the planner should see. Mirrored in ros2-control-bot's
 * collision_geometry.py; both read the flags at ros2-control-bot launch, which
 * is why every flag here requires a restart.
 */
export function isPrimitiveCollisionModelLoadedForModel(
  modelType: ModelTypeEnum | null | undefined,
  flags: PrimitiveCollisionModelFlags,
): boolean {
  return (
    flags.onlinePlannerJoggingCollisionAvoidance &&
    isOnlineJoggingActiveForModel(modelType, flags)
  );
}

/**
 * {@link isPrimitiveCollisionModelLoadedForModel} against this process's model
 * and flags.
 */
export function isPrimitiveCollisionModelLoadedForThisModel(): boolean {
  return (
    getFeatureFlagSync('onlinePlannerJoggingCollisionAvoidance', true) &&
    isOnlineJoggingActiveForThisModel()
  );
}
